/**
 * WorkspaceProvider.tsx -- Keystone provider that resolves the active workspace
 * and exposes it as a five-variant discriminated union through React context.
 *
 * Spec: specs/remediation-plan.md Section 2.3 (Fix B, the keystone).
 * Coverage: FR-004 (workspace identifier supplied to every workspace-scoped
 * screen) and FR-012 (correct router accessor — `useRouterContext`, not the
 * absent `useRouterLocation`).
 *
 * Architectural contract:
 *
 *   - Single instance of `useRepositoryList()` — NFR-004 forbids duplicating the
 *     repository-list hook. The provider is the sole consumer for the resolved
 *     workspace pathway.
 *   - URL slug read via `useRouterContext()` (the hash-router context). Direct
 *     `window.location.hash` access is forbidden by the
 *     `no-direct-hash-navigation` guard.
 *   - Persisted selection stored in `localStorage` under the canonical key
 *     `capicua.current-workspace-id`. Every storage access is wrapped in
 *     try/catch — privacy-mode browsers throw on `localStorage` read/write.
 *   - Resolution precedence: slug > persisted > first (sorted by `updatedAt`
 *     desc, as returned by `useRepositoryList`).
 *   - Sync effect runs at most once per stable resolution input. The guard
 *     compares the resolved id against BOTH the local `selectedId` state and
 *     the on-disk persisted value, so a re-render with unchanged inputs does
 *     not re-fire `setItem`.
 *   - Loading and empty branches are surfaced as discrete `status` values
 *     rather than as thrown errors — workspace-resolution failure must reach
 *     consumers as `empty` / `forbidden` / `error`, never as a thrown
 *     exception that the per-route boundary would have to catch.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  useRepositoryList,
  type RepositoryListItem,
} from "../../api/hooks/useRepositoryList";
import { useRouterContext } from "../../lib/router/Router";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/**
 * Workspace-scoped route shape, mirrored from `AppShell.tsx` so the two
 * extraction sites stay in lock-step. The capture group is the workspace slug.
 */
const WORKSPACE_RE = /^\/workspace\/([^/]+)/;

/** Canonical localStorage key for the persisted workspace selection. */
const STORAGE_KEY = "capicua.current-workspace-id";

/* ------------------------------------------------------------------ */
/*  Context type                                                       */
/* ------------------------------------------------------------------ */

/**
 * Five-variant discriminated union the provider exposes to consumers via
 * `useWorkspaceContext()`. The `status` field is the discriminant.
 *
 *   - `loading`   — repository list is still resolving.
 *   - `ready`     — resolved workspace is available; `workspaceId`, `slug`,
 *                   and `name` are present along with the full list for
 *                   switchers.
 *   - `empty`     — caller is authenticated but belongs to no workspaces.
 *   - `forbidden` — caller is not authorised to read any workspace (reserved
 *                   for future role-based gating; the provider itself does not
 *                   currently produce this branch).
 *   - `error`     — unrecoverable resolution failure (also reserved; today
 *                   localStorage failures degrade silently into the precedence
 *                   chain rather than surfacing as `error`).
 */
export type WorkspaceContextValue =
  | { status: "loading" }
  | { status: "empty" }
  | { status: "forbidden" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      workspaceId: string;
      slug: string;
      name: string;
      workspaces: RepositoryListItem[];
    };

const WorkspaceContext = createContext<WorkspaceContextValue>({
  status: "loading",
});

/* ------------------------------------------------------------------ */
/*  Public hook                                                        */
/* ------------------------------------------------------------------ */

/**
 * Reads the workspace context. Must be called from a descendant of
 * `<WorkspaceProvider>`. Returns `{ status: "loading" }` when called outside
 * the provider tree (the default context value), which the
 * `useCurrentWorkspace` flag-gated wrapper relies on to preserve rollback
 * behaviour when `workspace_context_v1` is off.
 */
export function useWorkspaceContext(): WorkspaceContextValue {
  return useContext(WorkspaceContext);
}

/* ------------------------------------------------------------------ */
/*  Storage helpers — every access wrapped in try/catch                */
/* ------------------------------------------------------------------ */

/**
 * Reads the persisted workspace id, returning `null` on any failure.
 * Privacy-mode browsers throw on `localStorage` access; the catch keeps the
 * provider rendering instead of crashing the application.
 */
function safeGetPersistedId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Writes the persisted workspace id, swallowing any failure. Quota-exceeded
 * and privacy-mode errors are non-actionable from the provider's perspective —
 * the active selection survives in-memory in `selectedId` either way.
 */
function safeSetPersistedId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* privacy mode, quota exceeded, or storage unavailable — silently drop */
  }
}

/* ------------------------------------------------------------------ */
/*  Slug extraction                                                    */
/* ------------------------------------------------------------------ */

/**
 * Extracts the workspace slug from a `/workspace/<slug>/...` path. Returns
 * `null` for paths that do not match. Mirrors the AppShell regex so the two
 * call sites can never drift.
 */
function extractWorkspaceSlug(path: string): string | null {
  const match = WORKSPACE_RE.exec(path);
  return match?.[1] ?? null;
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export interface WorkspaceProviderProps {
  children: ReactNode;
}

/**
 * Internal resolver result. `"loading"` and `"empty"` mirror the matching
 * context-value branches; an item resolves to `"ready"` at the context layer.
 */
type ResolverResult = RepositoryListItem | "loading" | "empty";

export function WorkspaceProvider({
  children,
}: WorkspaceProviderProps): React.ReactElement {
  const list = useRepositoryList();
  const { path } = useRouterContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const slug = extractWorkspaceSlug(path);

  /* ---- Resolve --------------------------------------------------- */

  /**
   * Runs in render so the provider's `value` is in-sync with the latest
   * repository-list snapshot on every render. The persisted id is read here
   * (rather than in the effect) because resolution itself depends on it —
   * the effect is purely about persisting the *result*, not computing it.
   */
  const resolved = useMemo<ResolverResult>(() => {
    if (list.status === "loading") {
      return "loading";
    }
    if (list.data.length === 0) {
      return "empty";
    }

    // (1) Slug match — wins over persisted and first.
    if (slug !== null) {
      const bySlug = list.data.find((r) => r.slug === slug);
      if (bySlug) {
        return bySlug;
      }
    }

    // (2) Persisted id — wins over first when slug missed.
    const persisted = safeGetPersistedId();
    if (persisted) {
      const byId = list.data.find(
        (r) => r.id === persisted || r._id === persisted,
      );
      if (byId) {
        return byId;
      }
    }

    // (3) First workspace (sorted by updatedAt desc upstream).
    const first = list.data[0];
    if (!first) {
      // Defensive: length > 0 was checked above, but `noUncheckedIndexedAccess`
      // widens the element type to `RepositoryListItem | undefined`.
      return "empty";
    }
    return first;
  }, [list, slug]);

  /* ---- Sync effect (no-thrash guard) ---------------------------- */

  /**
   * Persists the resolved id and mirrors it into `selectedId` state. The
   * skip-condition compares the resolved id against BOTH the in-state
   * `selectedId` and the on-disk persisted value — if either already matches
   * the resolved id, the write is already in flight (selectedId) or already
   * landed (on-disk), so we skip. This makes the effect idempotent against
   * re-renders with unchanged inputs (test (g) — no-thrash guard) and against
   * benign localStorage failures (test (h) — getItem-throws does not produce
   * an infinite write loop).
   */
  useEffect(() => {
    if (resolved === "loading" || resolved === "empty") {
      return;
    }

    const resolvedId = resolved.id;
    const onDisk = safeGetPersistedId();

    // Skip when EITHER source confirms the resolved id has already been
    // written or staged. This is the no-thrash guard.
    if (selectedId === resolvedId || onDisk === resolvedId) {
      // Even when skipping the write, make sure `selectedId` reflects the
      // resolved id so downstream consumers and the next effect run see a
      // consistent value.
      if (selectedId !== resolvedId) {
        setSelectedId(resolvedId);
      }
      return;
    }

    // First-time resolution (or external invalidation) — write through.
    safeSetPersistedId(resolvedId);
    setSelectedId(resolvedId);
  }, [resolved, selectedId]);

  /* ---- Context value -------------------------------------------- */

  const value = useMemo<WorkspaceContextValue>(() => {
    if (resolved === "loading") {
      return { status: "loading" };
    }
    if (resolved === "empty") {
      return { status: "empty" };
    }
    return {
      status: "ready",
      workspaceId: resolved.id,
      slug: resolved.slug,
      name: resolved.name,
      workspaces: list.status === "ready" ? list.data : [],
    };
  }, [resolved, list]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export default WorkspaceProvider;
